// src/components/DynamicForm.jsx
import React, { useState } from "react";
import {
  buildSubmissionFieldsFromFormData,
  getFieldSubmissionKey,
  notifySubmissionByEmail,
  saveFormSubmission,
} from "../utils/formSubmissions";

export default function DynamicForm({ form, notificationEmail = "" }) {
  const [result, setResult] = useState("");
  const [resultType, setResultType] = useState("success");
  const [submitting, setSubmitting] = useState(false);

  if (!form) return null;

  const fieldFontSize = Number(form.inputFontSize) || 14;
  const fieldPadding = `${Number(form.inputPaddingTop ?? 8)}px ${Number(
    form.inputPaddingRight ?? 10
  )}px ${Number(form.inputPaddingBottom ?? 8)}px ${Number(
    form.inputPaddingLeft ?? 10
  )}px`;
  const labelFontSize = Number(form.labelFontSize) || 13;
  const buttonFontSize = Number(form.buttonFontSize) || 14;
  const buttonPadding = `${Number(form.buttonPaddingTop ?? 10)}px ${Number(
    form.buttonPaddingRight ?? 18
  )}px ${Number(form.buttonPaddingBottom ?? 10)}px ${Number(
    form.buttonPaddingLeft ?? 18
  )}px`;
  const formPadding = `${Number(form.cardPaddingTop ?? form.cardPadding ?? 20)}px ${Number(
    form.cardPaddingRight ?? form.cardPadding ?? 20
  )}px ${Number(form.cardPaddingBottom ?? form.cardPadding ?? 20)}px ${Number(
    form.cardPaddingLeft ?? form.cardPadding ?? 20
  )}px`;

  const fields = Array.isArray(form.fields) ? form.fields : [];

  const onSubmit = async (event) => {
    event.preventDefault();
    setResult("");
    setResultType("success");
    if (submitting) return;

    const formData = new FormData(event.target);
    const submissionFields = buildSubmissionFieldsFromFormData(formData, fields);

    try {
      setSubmitting(true);
      await saveFormSubmission({
        formId: form.id || "",
        formName: form.formName || form.formTitle || "Website Form",
        formType: "dynamic_form",
        fields: submissionFields,
        metadata: {
          accessKeyConfigured: Boolean(form.accessKey),
          notificationEmail:
            String(notificationEmail || form.notificationEmail || "").trim() || null,
        },
      });

      let emailWarning = "";
      try {
        await notifySubmissionByEmail({
          accessKey: form.accessKey,
          toEmail: notificationEmail || form.notificationEmail,
          formName: form.formName || form.formTitle || "Website Form",
          fields: submissionFields,
          metadata: {
            formType: "dynamic_form",
          },
        });
      } catch (notifyError) {
        console.error("Web3Forms notification failed", notifyError);
        emailWarning = " Saved, but email notification failed.";
      }

      setResult(`${form.submitMessage || "Success!"}${emailWarning}`);
      setResultType(emailWarning ? "warning" : "success");
      event.target.reset();
    } catch (e) {
      console.error(e);
      setResult("Error. Please try again.");
      setResultType("error");
    } finally {
      setSubmitting(false);
    }
  };

  const bgStyle = {};
  if (form.bgType === "transparent") {
    bgStyle.background = "transparent";
  } else if (form.bgType === "color") {
    bgStyle.background = form.bgColor || "#f5f5f5";
  } else if (form.bgType === "image") {
    bgStyle.backgroundImage = `url(${form.bgImageUrl})`;
    bgStyle.backgroundSize = "cover";
    bgStyle.backgroundPosition = "center";
  }

  return (
    <form
      onSubmit={onSubmit}
      className="dynamic-form"
      style={{
        ...bgStyle,
        padding: formPadding,
        borderRadius: Number(form.cardRadius) || 14,
        border: `1px solid ${form.cardBorderColor || "rgba(0,0,0,0.06)"}`,
        backdropFilter: form.bgType === "transparent" ? "blur(4px)" : "none",
      }}
    >
      {form.formTitle && (
        <h2
          style={{
            margin: "0 0 6px",
            color: form.titleColor || "#111827",
            fontSize: `${Number(form.titleFontSize) || 22}px`,
            fontFamily: form.titleFontFamily || "inherit",
            fontWeight: form.titleFontWeight || 700,
            lineHeight: form.titleLineHeight || 1.2,
            letterSpacing:
              form.titleLetterSpacing !== undefined
                ? `${Number(form.titleLetterSpacing)}px`
                : undefined,
            textTransform: form.titleTextTransform || "none",
            textAlign: form.titleAlign || "left",
          }}
        >
          {form.formTitle}
        </h2>
      )}

      {form.formDetails && (
        <p
          style={{
            margin: "0 0 14px",
            color: form.messageColor || "#4b5563",
            fontSize: `${Number(form.messageFontSize) || 14}px`,
            fontFamily: form.messageFontFamily || "inherit",
            fontWeight: form.messageFontWeight || 400,
            lineHeight: form.messageLineHeight || 1.5,
            letterSpacing:
              form.messageLetterSpacing !== undefined
                ? `${Number(form.messageLetterSpacing)}px`
                : undefined,
            textTransform: form.messageTextTransform || "none",
            textAlign: form.messageAlign || "left",
          }}
        >
          {form.formDetails}
        </p>
      )}

      {fields.map((field, index) => {
        const inputName = getFieldSubmissionKey(field, index);
        const commonStyles = {
          padding: fieldPadding,
          borderRadius: Number(form.inputRadius ?? 8),
          border: `${Number(form.inputBorderWidth ?? 1)}px solid ${
            form.inputBorderColor || "#d1d5db"
          }`,
          fontSize: `${fieldFontSize}px`,
          fontFamily: form.inputFontFamily || "inherit",
          fontWeight: form.inputFontWeight || 400,
          lineHeight: form.inputLineHeight || 1.5,
          letterSpacing:
            form.inputLetterSpacing !== undefined
              ? `${Number(form.inputLetterSpacing)}px`
              : undefined,
          color: form.inputTextColor || "#111827",
          background: form.inputBg || "#ffffff",
          height:
            field.type === "textarea" ? undefined : `${Number(form.inputHeight) || 44}px`,
        };
        const isRequired = field.required !== false;

        let inputElement = (
          <input
            type={field.type || "text"}
            name={inputName}
            placeholder={field.placeholder}
            required={isRequired}
            style={commonStyles}
          />
        );

        if (field.type === "textarea") {
          inputElement = (
            <textarea
              name={inputName}
              placeholder={field.placeholder}
              required={isRequired}
              rows={4}
              style={{ ...commonStyles, minHeight: 90, resize: "vertical", height: "auto" }}
            />
          );
        } else if (field.type === "select") {
          const options = Array.isArray(field.options) ? field.options : [];
          inputElement = (
            <select
              name={inputName}
              required={isRequired}
              defaultValue=""
              style={commonStyles}
            >
              <option value="" disabled>
                {field.placeholder || `Select ${field.name || "option"}`}
              </option>
              {options.map((option, optIndex) => (
                <option key={`${field.id || field.name || "field"}-${optIndex}`} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );
        }

        return (
          <div
            key={field.id || inputName}
            style={{
              marginBottom: 12,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {field.name && (
              <label
                style={{
                  fontSize: `${labelFontSize}px`,
                  fontFamily: form.inputFontFamily || "inherit",
                  fontWeight: 500,
                  marginBottom: 6,
                  color: form.labelColor || "#111827",
                }}
              >
                {field.name}
              </label>
            )}
            {inputElement}
          </div>
        );
      })}

      <button
        type="submit"
        disabled={submitting}
        style={{
          marginTop: Number(form.buttonMarginTop ?? 4),
          padding: buttonPadding,
          borderRadius: Number(form.buttonRadius ?? 999),
          border: "none",
          cursor: "pointer",
          fontWeight: Number(form.buttonFontWeight) || 600,
          fontFamily: form.buttonFontFamily || "inherit",
          fontSize: `${buttonFontSize}px`,
          lineHeight: form.buttonLineHeight || 1.5,
          letterSpacing:
            form.buttonLetterSpacing !== undefined
              ? `${Number(form.buttonLetterSpacing)}px`
              : undefined,
          background: form.submitButtonColor || "#111827",
          color: form.buttonTextColor || "#fff",
          minWidth: `${Number(form.buttonMinWidth) || 120}px`,
          width: form.buttonWidth === "full" ? "100%" : "auto",
          height: `${Number(form.buttonHeight) || 44}px`,
          alignSelf:
            form.buttonAlign === "center"
              ? "center"
              : form.buttonAlign === "right"
              ? "flex-end"
              : "flex-start",
        }}
      >
        {submitting ? "Submitting..." : form.buttonText || "Submit"}
      </button>

      {result && (
        <p
          style={{
            marginTop: 10,
            fontSize: "13px",
            color:
              resultType === "error"
                ? "#b91c1c"
                : resultType === "warning"
                ? "#b45309"
                : "#059669",
          }}
        >
          {result}
        </p>
      )}
    </form>
  );
}
